import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api, asArray } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageBits";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function Blog() {
  useSEO({ title: "Blog — Tea Tips & Brewing Guides", description: "Read Leaf Alchemy's blog for tea tips, brewing guides, health benefits and B2B guides for restaurants and hotels." });
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    const load = () => {
      const params = {};
      if (cat !== "All") params.category = cat;
      if (q) params.q = q;
      api.get("/blog", { params })
        .then((r) => setPosts(asArray(r.data)))
        .catch(() => setPosts([]));
    };

    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [cat, q]);

  useEffect(() => {
    api.get("/blog/categories")
      .then((r) => setCats(["All", ...asArray(r.data)]))
      .catch(() => setCats(["All"]));
  }, []);

  return (
    <div data-testid="blog-page">
      <PageHero eyebrow="Journal" title="Tea Tips & Brewing Guides"
        subtitle="Stories, guides and insights from the world of premium tea." />

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-10">
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`blog-cat-${c}`}
                  className={`px-4 py-1.5 rounded-full text-sm transition-colors ${cat === c ? "bg-forest text-cream" : "bg-cream border border-forest/15 text-forest hover:border-gold"}`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/40" size={16} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles..." data-testid="blog-search"
                className="pl-9 bg-cream border-forest/15 focus-visible:ring-gold" />
            </div>
          </div>

          {posts.length === 0 ? (
            <p className="text-center text-muted-foreground py-20" data-testid="blog-empty">No articles found.</p>
          ) : (
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                    className="group block bg-cream rounded-2xl overflow-hidden border border-forest/10 hover:border-gold/40 transition-colors h-full">
                    <div className="h-52 overflow-hidden">
                      <img src={p.featured_image} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <Badge variant="outline" className="border-olive/40 text-olive mb-3">{p.category}</Badge>
                      <h3 className="font-display text-xl text-forest leading-snug group-hover:text-gold transition-colors">{p.title}</h3>
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.excerpt}</p>
                      <span className="inline-block mt-4 text-sm text-forest font-medium">Read more →</span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
